import React from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { AccessibilityProvider } from "@/context/AccessibilityContext";
import { Toaster } from "@/components/ui/sonner";
import { Loader2 } from "lucide-react";

import Layout from "@/components/Layout";
import Login from "@/pages/Login";
import ForgotPassword from "@/pages/ForgotPassword";
import SetupPassword from "@/pages/SetupPassword";
import Dashboard from "@/pages/Dashboard";
import CommandCenter from "@/pages/CommandCenter";
import Leads from "@/pages/Leads";
import Consultations from "@/pages/Consultations";
import Cases from "@/pages/Cases";
import CaseDetail from "@/pages/CaseDetail";
import Partners from "@/pages/Partners";
import PartnerDetail from "@/pages/PartnerDetail";
import Referrals from "@/pages/Referrals";
import TrilogyOutreach from "@/pages/TrilogyOutreach";
import Tasks from "@/pages/Tasks";
import AIAssistant from "@/pages/AIAssistant";
import ResourcePage from "@/components/ResourcePage";
import DocumentVault from "@/pages/DocumentVault";
import DocumentCenter from "@/pages/documents/DocumentCenter";
import DocumentDetail from "@/pages/documents/DocumentDetail";
import DocumentRequests from "@/pages/documents/DocumentRequests";
import DocumentReview from "@/pages/documents/DocumentReview";
import Revenue from "@/pages/Revenue";
import Reports from "@/pages/Reports";
import ReportingCenter from "@/pages/reports/center/ReportingCenter";
import SopLibrary from "@/pages/sops/SopLibrary";
import SopDetail from "@/pages/sops/SopDetail";
import SopEditor from "@/pages/sops/SopEditor";
import SopReview from "@/pages/sops/SopReview";
import SopSuggestions from "@/pages/sops/SopSuggestions";
import { HelpProvider } from "@/components/help/HelpProvider";
import HelpAssistantSettings from "@/pages/settings/HelpAssistantSettings";
import SettingsCommunications from "@/pages/settings/SettingsCommunications";
import SettingsRouting from "@/pages/settings/SettingsRouting";
import NotificationCenter from "@/pages/notifications/NotificationCenter";
import EscalationHealth from "@/pages/notifications/EscalationHealth";
import SecurityAuditLogs from "@/pages/security/SecurityAuditLogs";
import PrivacyPolicy from "@/pages/public/PrivacyPolicy";
import Terms from "@/pages/public/Terms";
import PrivacyRequest from "@/pages/public/PrivacyRequest";
import PrivacyRequests from "@/pages/settings/PrivacyRequests";
import TranscriptCleanup from "@/pages/security/TranscriptCleanup";
import SecurityDashboard from "@/pages/security/SecurityDashboard";
import SecurityDigest from "@/pages/settings/SecurityDigest";
import DemoDataCleanup from "@/pages/security/DemoDataCleanup";
import PortalNotifications from "@/pages/portal/PortalNotifications";
import ExternalNotifications from "@/pages/external/ExternalNotifications";
import Users from "@/pages/Users";
import Branches from "@/pages/Branches";
import BranchDetail from "@/pages/BranchDetail";
import Settings from "@/pages/Settings";
import MeetingSettings from "@/pages/settings/MeetingSettings";
import DocumentSettings from "@/pages/settings/DocumentSettings";
import DesignSystem from "@/pages/DesignSystem";
import AuditLogs from "@/pages/AuditLogs";
import MarketingOverview from "@/pages/marketing/MarketingOverview";
import MarketingAnalytics from "@/pages/marketing/MarketingAnalytics";
import MarketingForms from "@/pages/marketing/MarketingForms";
import Subscribers from "@/pages/marketing/Subscribers";
import Newsletters from "@/pages/marketing/Newsletters";
import Automations from "@/pages/marketing/Automations";
import EmailActivity from "@/pages/marketing/EmailActivity";
import { RESOURCES } from "@/config/resources";

import PortalLayout from "@/components/PortalLayout";
import PortalHome from "@/pages/portal/PortalHome";
import PortalDocuments from "@/pages/portal/PortalDocuments";
import PortalMeetings from "@/pages/portal/PortalMeetings";
import RoadmapPortal from "@/pages/portal/RoadmapPortal";
import ChecklistPortal from "@/pages/portal/ChecklistPortal";
import PortalMessages from "@/pages/portal/PortalMessages";
import ClientReport from "@/pages/portal/ClientReport";
import { DocumentsRoute, MessagesRoute, ReferralsRoute, ProfileRoute } from "@/pages/portal/PortalDispatch";
import PartnerReferrals from "@/pages/portal/PartnerReferrals";
import PartnerTasks from "@/pages/portal/PartnerTasks";
import PartnerNotes from "@/pages/portal/PartnerNotes";
import PartnerPayouts from "@/pages/portal/PartnerPayouts";
import PartnerProfile from "@/pages/portal/PartnerProfile";
import PurchaserOpportunities from "@/pages/portal/PurchaserOpportunities";
import PurchaserDocuments from "@/pages/portal/PurchaserDocuments";
import PurchaserDealRoom from "@/pages/portal/PurchaserDealRoom";

import ExternalLayout from "@/components/ExternalLayout";
import ExternalDashboard from "@/pages/external/ExternalDashboard";
import ExternalProfile from "@/pages/external/ExternalProfile";
import ExternalReferrals from "@/pages/external/ExternalReferrals";
import ExternalReferralDetail from "@/pages/external/ExternalReferralDetail";
import ExternalMeetings from "@/pages/external/ExternalMeetings";
import ExternalMeetingDetail from "@/pages/external/ExternalMeetingDetail";
import ExternalDocuments from "@/pages/external/ExternalDocuments";
import ExternalReviewsSignings from "@/pages/external/ExternalReviewsSignings";
import ExternalComingSoon from "@/pages/external/ExternalComingSoon";
import ExternalAccounts from "@/pages/ExternalAccounts";
import StaffExternalReferrals from "@/pages/StaffExternalReferrals";
import StaffExternalReferralDetail from "@/pages/StaffExternalReferralDetail";
import ExternalReferralsReport from "@/pages/ExternalReferralsReport";

import PublicLayout from "@/components/public/PublicLayout";
import Home from "@/pages/public/Home";
import Book from "@/pages/public/Book";
import Contact from "@/pages/public/Contact";
import ThankYou from "@/pages/public/ThankYou";
import ForFamilies from "@/pages/public/ForFamilies";
import ForProfessionals from "@/pages/public/ForProfessionals";
import TrilogyWellness from "@/pages/public/TrilogyWellness";
import ServiceLanePage from "@/pages/public/ServiceLanePage";
import ReverseSearchPage from "@/pages/public/ReverseSearchPage";
import LocalPage from "@/pages/public/LocalPage";
import LocalServiceLanePage from "@/pages/public/LocalServiceLanePage";
import ChatContinue from "@/pages/public/ChatContinue";
import Resources from "@/pages/public/Resources";
import FamilyFaq from "@/pages/public/FamilyFaq";
import FaqDetail from "@/pages/public/FaqDetail";
import GuestRoom from "@/pages/public/GuestRoom";
import Meetings from "@/pages/meetings/Meetings";
import MeetingDetail from "@/pages/meetings/MeetingDetail";
import SiteEditor from "@/pages/site/SiteEditor";
import MenuEditor from "@/pages/site/MenuEditor";
import ServiceLanes from "@/pages/site/ServiceLanes";
import ServiceLaneEditor from "@/pages/site/ServiceLaneEditor";
import LocalEditor from "@/pages/site/LocalEditor";
import ReverseSearch from "@/pages/site/ReverseSearch";
import ReverseSearchEditor from "@/pages/site/ReverseSearchEditor";
import IntakeAdmin from "@/pages/site/IntakeAdmin";
import FamilyFaqManager from "@/pages/site/FamilyFaqManager";
import ChatInbox from "@/pages/chat/ChatInbox";
import ChatAnalytics from "@/pages/chat/ChatAnalytics";
import ChatQA from "@/pages/chat/ChatQA";
import Testimonials from "@/pages/Testimonials";
import CrmPipeline from "@/pages/crm/CrmPipeline";
import CrmLeadDetail from "@/pages/crm/CrmLeadDetail";
import CrmFollowUps from "@/pages/crm/CrmFollowUps";
import CrmRelationships from "@/pages/crm/CrmRelationships";
import CrmRelationshipDetail from "@/pages/crm/CrmRelationshipDetail";
import CrmOpportunities from "@/pages/crm/CrmOpportunities";
import CrmOpportunityDetail from "@/pages/crm/CrmOpportunityDetail";
import CrmReports from "@/pages/crm/CrmReports";
import TerritoryReports from "@/pages/reports/TerritoryReports";
import SeoIntelligence from "@/pages/seo/SeoIntelligence";
import FranchiseAdmin from "@/pages/franchises/FranchiseAdmin";
import FranchiseDetail from "@/pages/franchises/FranchiseDetail";
import FranchiseContent from "@/pages/franchises/FranchiseContent";
import LocalProfileExport from "@/pages/franchises/LocalProfileExport";
import OverrideReview from "@/pages/franchises/OverrideReview";
import LegacyUnassigned from "@/pages/franchises/LegacyUnassigned";

const isPortal = (user) => !!user?.is_portal && !user?.is_external;
const isExternal = (user) => !!user?.is_external;
const isAdmin = (user) => !!user?.is_admin;

const FullLoader = () => (
  <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: "#F7FAFC" }}>
    <Loader2 className="animate-spin text-[#1C4358]" size={32} />
  </div>
);

const homeFor = (user) => (isExternal(user) ? "/external" : isPortal(user) ? "/portal" : "/dashboard");

const Protected = ({ children }) => {
  const { user, loading } = useAuth();
  const location = useLocation();
  if (loading) return <FullLoader />;
  if (!user) return <Navigate to="/login" replace state={{ from: location }} />;
  if (isExternal(user)) return <Navigate to="/external" replace />;
  if (isPortal(user)) return <Navigate to="/portal" replace />;
  return children;
};

const PortalProtected = ({ children }) => {
  const { user, loading } = useAuth();
  if (loading) return <FullLoader />;
  if (!user) return <Navigate to="/login" replace />;
  if (isExternal(user)) return <Navigate to="/external" replace />;
  if (!isPortal(user)) return <Navigate to="/dashboard" replace />;
  return children;
};

const ExternalProtected = ({ children }) => {
  const { user, loading } = useAuth();
  if (loading) return <FullLoader />;
  if (!user) return <Navigate to="/login" replace />;
  if (!isExternal(user)) return <Navigate to={homeFor(user)} replace />;
  return children;
};

const PublicOnly = ({ children }) => {
  const { user, loading } = useAuth();
  if (loading) return <FullLoader />;
  if (user) return <Navigate to={homeFor(user)} replace />;
  return children;
};

const AdminRoute = ({ children }) => {
  const { user } = useAuth();
  if (!isAdmin(user)) return <Navigate to="/dashboard" replace />;
  return children;
};

const SuperAdminRoute = ({ children }) => {
  const { user } = useAuth();
  if (user?.role !== "super_admin") return <Navigate to="/dashboard" replace />;
  return children;
};

function AppRoutes() {
  return (
    <Routes>
      {/* Public marketing website */}
      <Route element={<PublicLayout />}>
        <Route path="/" element={<Home />} />
        <Route path="/book" element={<Book />} />
        <Route path="/contact" element={<Contact />} />
        <Route path="/thank-you" element={<ThankYou />} />
        <Route path="/for-families" element={<ForFamilies />} />
        <Route path="/for-professionals" element={<ForProfessionals />} />
        <Route path="/trilogy-senior-wellness" element={<TrilogyWellness />} />
        <Route path="/services/:slug" element={<ServiceLanePage />} />
        <Route path="/find/:topic" element={<ReverseSearchPage />} />
        <Route path="/find/:topic/:location" element={<ReverseSearchPage />} />
        <Route path="/chat/continue" element={<ChatContinue />} />
        <Route path="/resources" element={<Resources />} />
        <Route path="/resources/faq" element={<FamilyFaq />} />
        <Route path="/resources/faq/:slug" element={<FaqDetail />} />
        <Route path="/privacy" element={<PrivacyPolicy />} />
        <Route path="/privacy-policy" element={<PrivacyPolicy />} />
        <Route path="/terms" element={<Terms />} />
        <Route path="/privacy/request" element={<PrivacyRequest />} />
        {/* PROMPT 14E — public local franchise pages (kept last; static routes above out-rank these) */}
        <Route path="/:country/:state/:city" element={<LocalPage />} />
        <Route path="/:country/:state/:city/:lane" element={<LocalServiceLanePage />} />
      </Route>

      <Route path="/login" element={<PublicOnly><Login /></PublicOnly>} />
      <Route path="/forgot-password" element={<PublicOnly><ForgotPassword /></PublicOnly>} />
      <Route path="/setup" element={<SetupPassword />} />
      <Route path="/reset-password" element={<SetupPassword />} />

      {/* Temporary / professional guest rooms (standalone, room-scoped) */}
      <Route path="/guest/room/:code" element={<GuestRoom />} />
      <Route path="/meeting/guest/:code" element={<GuestRoom />} />
      <Route path="/professional/meeting/:code" element={<GuestRoom />} />

      {/* Portals (family + partner + purchaser) */}
      <Route element={<PortalProtected><PortalLayout /></PortalProtected>}>
        <Route path="/portal" element={<PortalHome />} />
        <Route path="/portal/roadmap" element={<RoadmapPortal />} />
        <Route path="/portal/checklist" element={<ChecklistPortal />} />
        <Route path="/portal/report" element={<ClientReport />} />
        <Route path="/portal/meetings" element={<PortalMeetings />} />
        <Route path="/portal/documents" element={<DocumentsRoute />} />
        <Route path="/portal/messages" element={<MessagesRoute />} />
        <Route path="/portal/referrals" element={<ReferralsRoute />} />
        <Route path="/portal/tasks" element={<PartnerTasks />} />
        <Route path="/portal/notes" element={<PartnerNotes />} />
        <Route path="/portal/payouts" element={<PartnerPayouts />} />
        <Route path="/portal/profile" element={<ProfileRoute />} />
        <Route path="/portal/opportunities" element={<PurchaserOpportunities />} />
        <Route path="/portal/purchaser-documents" element={<PurchaserDocuments />} />
        <Route path="/portal/deal-room" element={<PurchaserDealRoom />} />
        <Route path="/portal/notifications" element={<PortalNotifications />} />
      </Route>

      {/* Unified external portal (partner / vendor / facility / professional) */}
      <Route element={<ExternalProtected><ExternalLayout /></ExternalProtected>}>
        <Route path="/external" element={<ExternalDashboard />} />
        <Route path="/external/profile" element={<ExternalProfile />} />
        <Route path="/external/referrals" element={<ExternalReferrals />} />
        <Route path="/external/referrals/:id" element={<ExternalReferralDetail />} />
        <Route path="/external/documents" element={<ExternalDocuments />} />
        <Route path="/external/reviews-signings" element={<ExternalReviewsSignings />} />
        <Route path="/external/meetings" element={<ExternalMeetings />} />
        <Route path="/external/meetings/:id" element={<ExternalMeetingDetail />} />
        <Route path="/external/messages" element={<ExternalComingSoon testid="external-messages-page" icon="MessagesSquare" title="Messages" note="Message threads live inside each referral and meeting. Open a referral to chat with your MovingMyMama team." />} />
        <Route path="/external/notifications" element={<ExternalNotifications />} />
      </Route>
      <Route path="/partner" element={<Navigate to="/external" replace />} />
      <Route path="/vendor" element={<Navigate to="/external" replace />} />
      <Route path="/facility" element={<Navigate to="/external" replace />} />
      <Route path="/professional" element={<Navigate to="/external" replace />} />

      {/* Staff CRM */}
      <Route element={<Protected><Layout /></Protected>}>
        <Route path="/dashboard" element={<Dashboard />} />
        <Route path="/command-center" element={<AdminRoute><CommandCenter /></AdminRoute>} />
        <Route path="/leads" element={<Leads />} />
        <Route path="/consultations" element={<Consultations />} />
        <Route path="/cases" element={<Cases />} />
        <Route path="/cases/:id" element={<CaseDetail />} />
        <Route path="/partners" element={<Partners />} />
        <Route path="/partners/:id" element={<PartnerDetail />} />
        <Route path="/referrals" element={<Referrals />} />
        <Route path="/trilogy-outreach" element={<TrilogyOutreach />} />
        <Route path="/tasks" element={<Tasks />} />
        <Route path="/chat" element={<ChatInbox />} />
        <Route path="/meetings" element={<Meetings />} />
        <Route path="/meetings/:id" element={<MeetingDetail />} />
        <Route path="/chat/analytics" element={<AdminRoute><ChatAnalytics /></AdminRoute>} />
        <Route path="/chat/qa" element={<AdminRoute><ChatQA /></AdminRoute>} />
        <Route path="/testimonials" element={<AdminRoute><Testimonials /></AdminRoute>} />
        <Route path="/external-accounts" element={<AdminRoute><ExternalAccounts /></AdminRoute>} />
        <Route path="/external-referrals" element={<StaffExternalReferrals />} />
        <Route path="/external-referrals/:id" element={<StaffExternalReferralDetail />} />
        <Route path="/reports/external-referrals" element={<ExternalReferralsReport />} />
        <Route path="/reports/territories" element={<TerritoryReports />} />
        <Route path="/reports/territories/:id" element={<TerritoryReports />} />
        <Route path="/crm/pipeline" element={<CrmPipeline />} />
        <Route path="/crm/follow-ups" element={<CrmFollowUps />} />
        <Route path="/crm/relationships" element={<CrmRelationships />} />
        <Route path="/crm/relationships/:id" element={<CrmRelationshipDetail />} />
        <Route path="/crm/opportunities" element={<CrmOpportunities />} />
        <Route path="/crm/opportunities/:id" element={<CrmOpportunityDetail />} />
        <Route path="/crm/reports" element={<AdminRoute><CrmReports /></AdminRoute>} />
        <Route path="/crm/leads/:id" element={<CrmLeadDetail />} />
        <Route path="/ai-assist" element={<AIAssistant />} />
        {Object.values(RESOURCES).filter((cfg) => cfg.key !== "leads" && cfg.key !== "consultations" && cfg.key !== "cases" && cfg.key !== "partners" && cfg.key !== "trilogy_outreach" && cfg.key !== "tasks").map((cfg) => (
          <Route key={cfg.key} path={cfg.path} element={<ResourcePage config={cfg} />} />
        ))}
        <Route path="/documents" element={<DocumentCenter />} />
        <Route path="/documents/requests" element={<DocumentRequests />} />
        <Route path="/documents/review" element={<DocumentReview />} />
        <Route path="/documents/vault" element={<DocumentVault />} />
        <Route path="/documents/:id" element={<DocumentDetail />} />
        <Route path="/revenue" element={<Revenue />} />
        <Route path="/reports" element={<AdminRoute><ReportingCenter /></AdminRoute>} />
        <Route path="/reports/classic" element={<AdminRoute><Reports /></AdminRoute>} />
        <Route path="/sops" element={<SopLibrary />} />
        <Route path="/sops/new" element={<AdminRoute><SopEditor /></AdminRoute>} />
        <Route path="/sops/review" element={<AdminRoute><SopReview /></AdminRoute>} />
        <Route path="/sops/suggestions" element={<AdminRoute><SopSuggestions /></AdminRoute>} />
        <Route path="/sops/:id" element={<SopDetail />} />
        <Route path="/sops/:id/edit" element={<AdminRoute><SopEditor /></AdminRoute>} />
        <Route path="/branches" element={<AdminRoute><Branches /></AdminRoute>} />
        <Route path="/branches/:id" element={<AdminRoute><BranchDetail /></AdminRoute>} />
        <Route path="/marketing" element={<AdminRoute><MarketingOverview /></AdminRoute>} />
        <Route path="/marketing/analytics" element={<AdminRoute><MarketingAnalytics /></AdminRoute>} />
        <Route path="/marketing/forms" element={<AdminRoute><MarketingForms /></AdminRoute>} />
        <Route path="/marketing/subscribers" element={<AdminRoute><Subscribers /></AdminRoute>} />
        <Route path="/marketing/newsletters" element={<AdminRoute><Newsletters /></AdminRoute>} />
        <Route path="/marketing/automations" element={<AdminRoute><Automations /></AdminRoute>} />
        <Route path="/marketing/email" element={<AdminRoute><EmailActivity /></AdminRoute>} />
        <Route path="/seo-intelligence" element={<AdminRoute><SeoIntelligence /></AdminRoute>} />
        <Route path="/users" element={<AdminRoute><Users /></AdminRoute>} />
        <Route path="/audit" element={<AdminRoute><AuditLogs /></AdminRoute>} />
        <Route path="/design-system" element={<SuperAdminRoute><DesignSystem /></SuperAdminRoute>} />
        <Route path="/site/homepage" element={<SuperAdminRoute><SiteEditor /></SuperAdminRoute>} />
        <Route path="/site/menus" element={<SuperAdminRoute><MenuEditor /></SuperAdminRoute>} />
        <Route path="/site/service-lanes" element={<AdminRoute><ServiceLanes /></AdminRoute>} />
        <Route path="/site/service-lanes/:id" element={<AdminRoute><ServiceLaneEditor /></AdminRoute>} />
        <Route path="/site/reverse-search" element={<AdminRoute><ReverseSearch /></AdminRoute>} />
        <Route path="/site/reverse-search/:id" element={<AdminRoute><ReverseSearchEditor /></AdminRoute>} />
        <Route path="/site/forms" element={<AdminRoute><IntakeAdmin /></AdminRoute>} />
        <Route path="/site/faq" element={<AdminRoute><FamilyFaqManager /></AdminRoute>} />
        <Route path="/site/local" element={<AdminRoute><LocalEditor /></AdminRoute>} />
        <Route path="/franchise-admin" element={<AdminRoute><FranchiseAdmin /></AdminRoute>} />
        <Route path="/franchise-admin/overrides/review" element={<AdminRoute><OverrideReview /></AdminRoute>} />
        <Route path="/franchise-admin/legacy" element={<AdminRoute><LegacyUnassigned /></AdminRoute>} />
        <Route path="/franchise-admin/:id" element={<AdminRoute><FranchiseDetail /></AdminRoute>} />
        <Route path="/franchise-admin/:id/content" element={<AdminRoute><FranchiseContent /></AdminRoute>} />
        <Route path="/franchise-admin/:id/local-profile-export" element={<AdminRoute><LocalProfileExport /></AdminRoute>} />
        <Route path="/settings" element={<Settings />} />
        <Route path="/settings/meetings" element={<AdminRoute><MeetingSettings /></AdminRoute>} />
        <Route path="/settings/documents" element={<AdminRoute><DocumentSettings /></AdminRoute>} />
        <Route path="/settings/help-assistant" element={<AdminRoute><HelpAssistantSettings /></AdminRoute>} />
        <Route path="/settings/communications" element={<SettingsCommunications />} />
        <Route path="/settings/notification-routing" element={<AdminRoute><SettingsRouting /></AdminRoute>} />
        <Route path="/notifications" element={<NotificationCenter />} />
        <Route path="/notifications/escalation-health" element={<AdminRoute><EscalationHealth /></AdminRoute>} />
        <Route path="/security" element={<AdminRoute><SecurityDashboard /></AdminRoute>} />
        <Route path="/security/audit-logs" element={<AdminRoute><SecurityAuditLogs /></AdminRoute>} />
        <Route path="/settings/privacy-requests" element={<AdminRoute><PrivacyRequests /></AdminRoute>} />
        <Route path="/security/transcript-cleanup" element={<AdminRoute><TranscriptCleanup /></AdminRoute>} />
        <Route path="/settings/security-digest" element={<AdminRoute><SecurityDigest /></AdminRoute>} />
        <Route path="/security/demo-data-cleanup" element={<SuperAdminRoute><DemoDataCleanup /></SuperAdminRoute>} />
      </Route>

      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

export default function App() {
  return (
    <div className="App">
      <AccessibilityProvider>
        <AuthProvider>
          <BrowserRouter>
            <HelpProvider>
              <AppRoutes />
            </HelpProvider>
          </BrowserRouter>
          <Toaster position="top-right" richColors closeButton />
        </AuthProvider>
      </AccessibilityProvider>
    </div>
  );
}
